import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Spinner, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMapMarkerAlt, faShieldAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

// Interface for address suggestions
export interface AddressSuggestion {
  address: string;
  coordinates: [number, number] | null;
  confidence: number;
  source: string;
}

// Props for the component
interface AddressAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  locked?: boolean;
  onUnlock?: () => void;
  className?: string;
  style?: React.CSSProperties;
  showClearButton?: boolean;
  label?: string;
  error?: string;
  required?: boolean;
}

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Enter property address...",
  disabled = false,
  locked = false,
  onUnlock,
  className = "",
  style = {},
  showClearButton = true,
  label,
  error,
  required = false
}) => {
  // State management
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch address suggestions from backend
  const fetchAddressSuggestions = async (query: string): Promise<AddressSuggestion[]> => {
    if (query.length < 3) return [];
    
    try {
      setIsLoadingSuggestions(true);
      const response = await axiosInstance.get(`/properties/address-suggestions-public?query=${encodeURIComponent(query)}`);
      
      if (response.data.success && response.data.suggestions) {
        return response.data.suggestions;
      }
      return [];
    } catch (error) {
      console.error('Address suggestions failed:', error);
      return [];
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Handle address input changes
  const handleAddressChange = async (inputValue: string) => {
    onChange(inputValue);
    
    if (inputValue.length >= 3 && !locked) {
      const fetchedSuggestions = await fetchAddressSuggestions(inputValue);
      setSuggestions(fetchedSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle address selection from suggestions
  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    onChange(suggestion.address);
    setSuggestions([]);
    setShowSuggestions(false);
    
    if (onAddressSelect) {
      onAddressSelect(suggestion);
    }
  };

  // Handle clear button
  const handleClear = () => {
    onChange('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle unlock (change address)
  const handleUnlock = () => {
    if (onUnlock) {
      onUnlock();
    }
  };

  // Hide suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowSuggestions(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={`position-relative ${className}`} style={style}>
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      
      <div onClick={(e) => e.stopPropagation()}>
        <InputGroup>
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => handleAddressChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled || locked}
            isInvalid={!!error}
            style={{
              borderRight: 'none',
              boxShadow: 'none',
              borderRadius: locked ? '6px' : '6px 0 0 6px'
            }}
          />
          
          {!locked && (
            <InputGroup.Text 
              style={{ 
                background: 'transparent', 
                border: 'none',
                borderLeft: 'none'
              }}
            >
              {isLoadingSuggestions ? (
                <Spinner size="sm" style={{ color: '#00d4ff' }} />
              ) : (
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              )}
            </InputGroup.Text>
          )}

          {locked && (
            <InputGroup.Text 
              style={{ 
                background: 'transparent', 
                border: 'none',
                borderLeft: 'none'
              }}
            >
              <FontAwesomeIcon icon={faShieldAlt} className="text-success" />
            </InputGroup.Text>
          )}

          {showClearButton && value && !locked && (
            <InputGroup.Text 
              onClick={handleClear}
              style={{ 
                background: 'transparent', 
                border: 'none',
                borderLeft: 'none',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="text-muted" />
            </InputGroup.Text>
          )}
        </InputGroup>

        {error && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {error}
          </Form.Control.Feedback>
        )}
        
        {/* Help text for address input */}
        {!locked && value.length > 0 && value.length < 3 && (
          <small className="text-muted d-block mt-1">
            Type at least 3 characters to see suggestions
          </small>
        )}

        {/* Address Suggestions Dropdown */}
        {suggestions.length > 0 && showSuggestions && !locked && (
          <div 
            className="position-absolute w-100 mt-1"
            style={{ zIndex: 1000 }}
          >
            <Card className="border-0 shadow-lg">
              <Card.Body className="p-0">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-3 border-bottom cursor-pointer"
                    style={{
                      cursor: 'pointer',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)'
                    }}
                    onClick={() => handleAddressSelect(suggestion)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-grow-1">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary me-2" />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{suggestion.address}</div>
                          <small className="text-muted">{suggestion.source === 'mapbox' ? 'Mapbox' : 'Google'}</small>
                        </div>
                      </div>
                      <div className="text-end ms-3">
                        <span 
                          className={`badge ${
                            suggestion.confidence >= 0.9 ? 'bg-success' : 
                            suggestion.confidence >= 0.7 ? 'bg-warning' : 
                            'bg-danger'
                          }`}
                        >
                          {(suggestion.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </div>
        )}

        {/* Change Address Button */}
        {locked && onUnlock && (
          <div className="mt-2 text-end">
            <Button 
              variant="outline-secondary" 
              onClick={handleUnlock}
              size="sm"
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Change Address
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressAutocompleteInput; 